import {
  Image as ImageType,
  ImageState,
  isContentImage,
  TeaserImage,
  TitleImage,
  ContentImage,
  TopHeadlineImage,
  TopHeadlineImageSmall,
  ImageGallery,
  AppLibraryImage,
  PortalAvatar,
  PortalLogo,
} from '../../../types/api/ImageType';
import { useSonTranslation } from '../../../translation/useSonTranslation';

export type ImageWithoutAvailableWidths =
  | TeaserImage
  | TitleImage
  | ContentImage
  | TopHeadlineImage
  | TopHeadlineImageSmall
  | ImageGallery
  | AppLibraryImage;

export function useImage(image: ImageType, emptyAltText?: boolean, customAltText?: string, hasPopUp?: boolean) {
  const { t } = useSonTranslation('Image');

  function hasAltText(imageObj: unknown): imageObj is { alt: string } {
    return !!(imageObj as { alt: string })?.alt;
  }

  function hasCopyright(imageObj: unknown): imageObj is { copyright: string } {
    return !!(imageObj as { copyright: string })?.copyright;
  }

  /**
   * Gets the alt attribute for <img>. It can be
   *   - empty alt (images that have no content meaning)
   *   - custom alt
   *   - portalAvatar & portalLogo always have the same alt
   *   - default images have different alt, depending on imageType
   *   - if alt is defined in props, we return this
   * @return {String|string|*}
   */
  const getAlt = (): string => {
    // priority: reset alt text to empty string if this is set true
    if (emptyAltText) {
      return '';
    }

    // set custom alt text overwriting stuff from the backend
    // especially, if you want to link an image
    if (customAltText) {
      return customAltText;
    }

    // portalAvatar and portalLogo always have the same alt text
    if (['portalAvatar', 'portalLogo'].includes(image.imageType)) {
      return t(`alternativeText.${(image as PortalAvatar | PortalLogo).imageType}`);
    }

    // default alt text for default or preview images
    if (image.imageState === ImageState.defaultImage) {
      return t(`alternativeText.${(image as ContentImage).imageType}`);
    }

    return hasAltText(image) ? image.alt : '';
  };

  const getAvailableWidthOfImage = (imageObj: ImageWithoutAvailableWidths): number[] => {
    return imageObj?.availableWidths;
  };

  const getWidthWithQueryParameter = (maxSize?: number): number | undefined => {
    const availableWidths = getAvailableWidthOfImage(image as ImageWithoutAvailableWidths);

    if (!availableWidths || availableWidths.length === 0) {
      return undefined;
    }

    if (maxSize && availableWidths.includes(maxSize)) {
      return maxSize;
    }

    return Math.max(...availableWidths);
  };

  /**
   * Decides if this image has a pop up (button).
   * It has one:
   * - when it's a content image
   * - AND when it's not an image as part of a slider in a pop up already
   */
  const hasPopUpButton = () => {
    if (isContentImage(image) && !hasPopUp) {
      return image.popUp;
    }
    return false;
  };

  return {
    hasCopyright,
    getAlt,
    getWidthWithQueryParameter,
    hasPopUpButton,
    getAvailableWidthOfImage,
  };
}
